/**
 * Study readiness — the "100% ready" gate behind the reader page's four
 * states (empty / loading / active / error), AP-778.
 *
 * A study is READY when every required gate for it reports true:
 *
 *   - 'viewer'     — Cornerstone engine initialized AND every visible pane has
 *                    finished its first load (media URLs resolved, viewport
 *                    created, content attached). Reported by
 *                    MultiViewerContainer from the pane-loading aggregate.
 *   - 'worksheet'  — the worksheet template AND the study's report have both
 *                    settled, so the panel renders labels + values in one
 *                    commit, not label-then-value pops. Reported by Report.
 *   - 'thumbnails' — every DICOM-list thumbnail image has SETTLED (loaded or
 *                    errored; a broken thumbnail's fallback is its final
 *                    state). Reported by DicomList.
 *
 * 'thumbnails' is a SECONDARY gate: it blocks the reveal today
 * (all-or-nothing, AP-778 §6) but is behind a toggle so a later write can
 * trade completeness for reveal latency without re-plumbing anything.
 *
 * Gates are keyed by studyUuid so a stale tree (the previous study, still
 * mounted under the loading overlay) can keep reporting without corrupting
 * the readiness of the study actually being loaded.
 *
 * Deliberately-background work (PDF prefetch, copilot sync, view tracking…)
 * must NEVER gate readiness — see studyBackgroundWork.ts, the manifest of
 * every non-blocking action on the page.
 *
 * The default context value is a no-op so Report / the viewer can render in
 * hosts without a provider (detached popup window, other pages, tests).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type StudyGate = 'viewer' | 'worksheet' | 'thumbnails';

/** Secondary-content gate(s). Set BLOCK_ON_SECONDARY_GATES to false to
 *  reveal without waiting for them (optimization lever — AP-778 §6 keeps the
 *  all-or-nothing default; flip only as a deliberate spec change). */
const SECONDARY_GATES: StudyGate[] = ['thumbnails'];
const BLOCK_ON_SECONDARY_GATES = true;

type GateMap = Record<string, Partial<Record<StudyGate, boolean>>>;

interface StudyReadinessValue {
  gatesByStudy: GateMap;
  reportGate: (studyUuid: string, gate: StudyGate, ready: boolean) => void;
  /** Forget a study's gates. Called when the selection LEAVES a study, so a
   *  later return to it starts from a clean slate instead of stale `ready`
   *  flags revealing it before its remounted tree has actually warmed up. */
  resetGates: (studyUuid: string) => void;
}

const StudyReadinessContext = createContext<StudyReadinessValue>({
  gatesByStudy: {},
  reportGate: () => {},
  resetGates: () => {},
});

export function StudyReadinessProvider({ children }: { children: ReactNode }) {
  const [gatesByStudy, setGatesByStudy] = useState<GateMap>({});

  const reportGate = useCallback(
    (studyUuid: string, gate: StudyGate, ready: boolean) => {
      setGatesByStudy((prev) => {
        if (prev[studyUuid]?.[gate] === ready) return prev;
        return {
          ...prev,
          [studyUuid]: { ...prev[studyUuid], [gate]: ready },
        };
      });
    },
    []
  );

  const resetGates = useCallback((studyUuid: string) => {
    setGatesByStudy((prev) => {
      if (!(studyUuid in prev)) return prev;
      const next = { ...prev };
      delete next[studyUuid];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ gatesByStudy, reportGate, resetGates }),
    [gatesByStudy, reportGate, resetGates]
  );

  return (
    <StudyReadinessContext.Provider value={value}>
      {children}
    </StudyReadinessContext.Provider>
  );
}

export function useStudyReadiness(): StudyReadinessValue {
  return useContext(StudyReadinessContext);
}

/** Whether every required gate for `studyUuid` reports ready. In detached
 *  mode the viewer and thumbnails live in a popup window (a separate React
 *  root that can't reach this provider), so only the worksheet gate is
 *  required. */
export function useStudyGatesReady(
  studyUuid: string | undefined,
  { requireViewer = true }: { requireViewer?: boolean } = {}
): boolean {
  const { gatesByStudy } = useStudyReadiness();
  if (!studyUuid) return false;
  const gates = gatesByStudy[studyUuid];
  const required: StudyGate[] = requireViewer
    ? [
        'viewer',
        'worksheet',
        ...(BLOCK_ON_SECONDARY_GATES ? SECONDARY_GATES : []),
      ]
    : ['worksheet'];
  return required.every((gate) => !!gates?.[gate]);
}
